/**
 * Global Keyboard Shortcut Listener
 * Listens for keyboard shortcuts and executes corresponding actions
 */

import { useEffect, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PREDEFINED_ACTIONS, PredefinedAction } from '../../modules/master-data/KeyboardShortcut/types/predefinedActions';
import { KEYBOARD_SHORTCUT_CONFIG } from '../../modules/master-data/KeyboardShortcut/config';
import { scrollSidebarToModule } from '../utils/sidebarUtils';
import logger from '../utils/logger';

// Type for stored shortcuts
interface StoredShortcut {
  actionId: string;
  shortcut: string;
}

interface KeyboardShortcutListenerProps {
  enabled?: boolean;
}

const KeyboardShortcutListener: React.FC<KeyboardShortcutListenerProps> = ({ enabled = true }) => {
  const navigate = useNavigate();
  const [shortcuts, setShortcuts] = useState<Map<string, PredefinedAction>>(new Map());
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  // Build shortcuts map on mount and when localStorage changes
  useEffect(() => {
    const buildShortcutsMap = () => {
      const shortcutsMap = new Map<string, PredefinedAction>();

      // Load stored shortcuts
      const saved = localStorage.getItem(KEYBOARD_SHORTCUT_CONFIG.STORAGE_KEY);
      let storedShortcuts: StoredShortcut[] = [];
      if (saved) {
        try {
          storedShortcuts = JSON.parse(saved);
        } catch (e) {
          logger.error('Failed to parse stored shortcuts:', e);
        }
      }

      // Map actions to their shortcuts
      PREDEFINED_ACTIONS.forEach(action => {
        const stored = storedShortcuts.find(s => s.actionId === action.id);
        const shortcut = stored?.shortcut || action.defaultShortcut;

        if (shortcut && shortcut !== '-') {
          shortcutsMap.set(shortcut.toLowerCase(), action);
        }
      });

      setShortcuts(shortcutsMap);
    };

    buildShortcutsMap();

    // Listen for storage changes (in case user updates shortcuts in another tab)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === KEYBOARD_SHORTCUT_CONFIG.STORAGE_KEY) {
        buildShortcutsMap();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Handle keyboard events
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when typing in inputs
    const target = e.target as HTMLElement;
    if (
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable
    ) {
      return;
    }

    // Build key string
    const keys: string[] = [];
    if (e.ctrlKey) keys.push('ctrl');
    if (e.altKey) keys.push('alt');
    if (e.shiftKey) keys.push('shift');
    if (e.metaKey) keys.push('meta');

    let key = e.key.toLowerCase();
    if (key === ' ') key = 'space';
    else if (key.startsWith('arrow')) key = key.replace('arrow', '');

    // Don't process modifier-only keys
    if (['control', 'alt', 'shift', 'meta'].includes(key)) {
      return;
    }

    keys.push(key);
    const pressedShortcut = keys.join('+');

    // Find matching action
    const action = shortcuts.get(pressedShortcut);
    if (action) {
      e.preventDefault();
      e.stopPropagation();

      // Execute action
      if (action.id === 'action_logout') {
        // Show confirmation for logout
        setShowLogoutConfirm(true);
      } else if (action.route) {
        navigate(action.route);
        // Scroll sidebar to show the module
        // We pass the action.id first as it might match a main-item ID better,
        // otherwise passing the route serves as a fallback for link matching.
        // Or simply passing action.id is safer if main-item attributes use IDs.
        // Based on sidebarUtils logic, it checks main-item attribute and hrefs.
        // Let's pass the route since that's what we are navigating to.
        setTimeout(() => {
           scrollSidebarToModule(action.route!);
        }, 100);
      } else if (action.id === 'action_refresh') {
        window.location.reload();
      }

      logger.debug(`[KeyboardShortcut] Executed: ${action.name}`);
    }
  }, [enabled, shortcuts, navigate]);

  useEffect(() => {
    if (enabled) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [enabled, handleKeyDown]);

  // Handle logout confirmation
  const handleLogoutConfirm = useCallback(() => {
    setShowLogoutConfirm(false);
    navigate('/logout');
  }, [navigate]);

  const handleLogoutCancel = useCallback(() => {
    setShowLogoutConfirm(false);
  }, []);

  // Handle Escape key to close modal
  useEffect(() => {
    if (!showLogoutConfirm) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowLogoutConfirm(false);
      } else if (e.key === 'Enter') {
        handleLogoutConfirm();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [showLogoutConfirm, handleLogoutConfirm]);

  // Render logout confirmation modal
  if (showLogoutConfirm) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={handleLogoutCancel}
        />

        {/* Modal */}
        <div className="relative bg-white dark:bg-zink-700 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
          {/* Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </div>
          </div>

          {/* Content */}
          <h3 className="text-lg font-semibold text-center text-slate-900 dark:text-zink-50 mb-2">
            Logout Confirmation
          </h3>
          <p className="text-sm text-center text-slate-600 dark:text-zink-300 mb-6">
            Are you sure you want to logout? You will need to login again to access the application.
          </p>

          {/* Buttons */}
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleLogoutCancel}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-zink-200 bg-slate-100 dark:bg-zink-600 hover:bg-slate-200 dark:hover:bg-zink-500 rounded-md transition-colors"
            >
              Cancel
              <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500 dark:text-zink-400 bg-white dark:bg-zink-700 border border-slate-300 dark:border-zink-500 rounded">
                Esc
              </kbd>
            </button>
            <button
              onClick={handleLogoutConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
            >
              Logout
              <kbd className="ml-2 px-1.5 py-0.5 text-[10px] font-semibold text-red-200 bg-red-600 border border-red-400 rounded">
                Enter
              </kbd>
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default KeyboardShortcutListener;
